/**
 * Fleet ROI Calculator
 * Self-contained custom element. All DOM lookups are scoped to this element
 * instance via `data-fleet-roi="..."` attributes (never global `id`s), so the
 * section can safely be placed more than once on the same page.
 *
 * Default values are read from `data-default-*` attributes rendered by the
 * section's Liquid schema settings, falling back to sensible constants if a
 * particular attribute is missing.
 * @module fleet-roi-calculator
 */

const INPUT_IDS = [
  "fleetSize",
  "hourlyLaborCost",
  "dailyChargingCost",
  "towCarts",
  "batteryReplacementCost"
];

const BASELINE_IDS = [
  "legacyLaborPerCart",
  "legacyBatteryInterval",
  "retrofitKitCost",
  "postRetroDailyCharging",
  "postRetroAnnualMaintenance",
  "retrofitLifespan"
];

const FALLBACK_DEFAULTS = {
  fleetSize: 60,
  hourlyLaborCost: 12,
  dailyChargingCost: 1.5,
  towCarts: "yes",
  batteryReplacementCost: 1500,
  legacyLaborPerCart: 363.54,
  legacyBatteryInterval: 3,
  retrofitKitCost: 2999,
  postRetroDailyCharging: 0.05,
  postRetroAnnualMaintenance: 15,
  retrofitLifespan: 10
};

const ASSUMED_RETRO_LABOR_HOURS_PER_CART = 2.4;
const TOW_DAMAGE_COST_PER_CART_PER_YEAR = 600;
const GROUNDS_DAMAGE_COST_PER_CART_PER_YEAR = 120;

const COLOR_SET = ["#1f8f3a", "#3ea0e8", "#f39f3d", "#8759c6", "#17a2a0"];

class FleetRoiCalculator extends HTMLElement {
  #controller = new AbortController();

  #costTrendChart;
  #annualOpsChart;
  #legacyBreakdownChart;
  #retroBreakdownChart;

  #moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  #percentFormatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  connectedCallback() {
    const { signal } = this.#controller;

    this.ref("resetDefaults")?.addEventListener("click", () => this.setDefaults(), { signal });
    this.ref("unlockBaseline")?.addEventListener("change", (event) => {
      this.setBaselineEditable(event.target.checked);
    }, { signal });
    this.ref("editInputs")?.addEventListener("click", () => this.showInputs(), { signal });
    this.ref("calculatorForm")?.addEventListener("submit", (event) => {
      event.preventDefault();
      this.calculate();
      this.showResults();
    }, { signal });

    this.setDefaults();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#costTrendChart?.destroy();
    this.#annualOpsChart?.destroy();
    this.#legacyBreakdownChart?.destroy();
    this.#retroBreakdownChart?.destroy();
  }

  /** Scoped lookup: finds a `data-fleet-roi="name"` element within this instance only. */
  ref(name) {
    return this.querySelector(`[data-fleet-roi="${name}"]`);
  }

  defaultFor(id) {
    if (id === "towCarts") {
      return this.dataset.defaultTowCarts || FALLBACK_DEFAULTS.towCarts;
    }
    const key = `default${id.charAt(0).toUpperCase()}${id.slice(1)}`;
    const value = Number(this.dataset[key]);
    return Number.isFinite(value) ? value : FALLBACK_DEFAULTS[id];
  }

  getNumber(id) {
    const value = Number(this.ref(id)?.value);
    return Number.isFinite(value) ? value : 0;
  }

  setDefaults() {
    INPUT_IDS.forEach((id) => {
      const node = this.ref(id);
      if (node) {
        node.value = this.defaultFor(id);
      }
    });

    BASELINE_IDS.forEach((id) => {
      const node = this.ref(id);
      if (node) {
        node.value = this.defaultFor(id);
      }
    });

    this.setBaselineEditable(false);
  }

  setBaselineEditable(unlocked) {
    const baselinePanel = this.ref("baselineInputs");
    const baselineToggle = this.ref("unlockBaseline");

    if (!baselinePanel || !baselineToggle) {
      return;
    }

    baselinePanel.classList.toggle("d-none", !unlocked);
    baselineToggle.checked = unlocked;
    baselineToggle.setAttribute("aria-expanded", String(unlocked));

    BASELINE_IDS.forEach((id) => {
      const node = this.ref(id);
      if (node) {
        node.readOnly = !unlocked;
      }
    });
  }

  fmtMoney(amount) {
    return this.#moneyFormatter.format(amount);
  }

  fmtMoneyNegative(amount) {
    return this.#moneyFormatter.format(-Math.abs(amount));
  }

  fmtMoneyCompact(amount) {
    if (!Number.isFinite(amount)) {
      return "N/A";
    }

    const abs = Math.abs(amount);
    const sign = amount < 0 ? "-" : "";

    if (abs >= 1000000) {
      return `${sign}$${(abs / 1000000).toFixed(3)}M`;
    }

    if (abs >= 1000) {
      return `${sign}$${Math.round(abs / 1000)}K`;
    }

    return `${sign}${this.#moneyFormatter.format(abs)}`;
  }

  fmtYears(value) {
    if (!Number.isFinite(value) || value < 0) {
      return "N/A";
    }
    return String(value);
  }

  updateCell(id, value, formatter) {
    const node = this.ref(id);
    if (!node) {
      return;
    }
    node.textContent = (formatter || this.fmtMoney.bind(this))(value);
  }

  showResults() {
    const stepInput = this.ref("stepInput");
    const stepResults = this.ref("stepResults");
    stepInput?.classList.add("section-hidden");
    stepResults?.classList.remove("section-hidden");
    stepResults?.scrollIntoView({ behavior: "smooth", block: "start" });
    this.#toggleFollowingSections(true);
  }

  showInputs() {
    const stepInput = this.ref("stepInput");
    const stepResults = this.ref("stepResults");
    stepResults?.classList.add("section-hidden");
    stepInput?.classList.remove("section-hidden");
    stepInput?.scrollIntoView({ behavior: "smooth", block: "start" });
    this.#toggleFollowingSections(false);
  }

  #toggleFollowingSections(visible) {
    let sibling = this.closest(".shopify-section")?.nextElementSibling;
    while (sibling) {
      sibling.classList.toggle("fleet-roi-post-calc-visible", visible);
      sibling = sibling.nextElementSibling;
    }
  }


  renderCharts(metrics) {
    if (window.Chart && window.ChartDataLabels && !this.constructor.datalabelsRegistered) {
      window.Chart.register(window.ChartDataLabels);
      this.constructor.datalabelsRegistered = true;
    }

    const years = Math.max(1, Math.round(metrics.retrofitLifespan));
    const labels = [];
    const legacySeries = [];
    const retroSeries = [];

    for (let year = 1; year <= years; year += 1) {
      labels.push(String(year));
      legacySeries.push(metrics.legacyTotal * year);
      retroSeries.push(metrics.retroTotal * year);
    }

    const chartCostTrend = this.ref("chartCostTrend");
    if (this.#costTrendChart) {
      this.#costTrendChart.destroy();
    }
    if (chartCostTrend) {
      this.#costTrendChart = new window.Chart(chartCostTrend, {
        type: "line",
        data: {
          labels,
          datasets: [
            {
              label: "Legacy Fleet (Current)",
              data: legacySeries,
              borderColor: "#5f6f64",
              backgroundColor: "rgba(95, 111, 100, 0.14)",
              borderWidth: 3,
              tension: 0.25,
              fill: false,
              pointRadius: 3
            },
            {
              label: "Retrofitted Fleet (New)",
              data: retroSeries,
              borderColor: "#FAB018",
              backgroundColor: "rgba(31, 143, 58, 0.15)",
              borderWidth: 3,
              tension: 0.25,
              fill: false,
              pointRadius: 3
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            datalabels: { display: false },
            legend: {
              position: "top",
              align: "start",
              labels: {
                usePointStyle: true,
                pointStyle: "line"
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${this.fmtMoney(context.raw)}`
              }
            }
          },
          scales: {
            y: {
              ticks: {
                callback: (value) => this.fmtMoney(value)
              }
            }
          }
        }
      });
    }

    const chartAnnualOps = this.ref("chartAnnualOps");
    if (this.#annualOpsChart) {
      this.#annualOpsChart.destroy();
    }
    if (chartAnnualOps) {
      const opsCategories = [
        { label: "Charging", legacy: metrics.legacyCharging, retro: metrics.retroCharging },
        { label: "Labor", legacy: metrics.legacyLabor, retro: metrics.retroLabor },
        { label: "Towing", legacy: metrics.legacyTowDamage, retro: metrics.retroTowDamage },
        { label: "Grounds", legacy: metrics.legacyGroundsDamage, retro: metrics.retroGroundsDamage },
        { label: "Battery", legacy: metrics.legacyBattery, retro: metrics.retroBattery }
      ].filter((category) => category.legacy > 0 || category.retro > 0);

      this.#annualOpsChart = new window.Chart(chartAnnualOps, {
        type: "bar",
        data: {
          labels: opsCategories.map((category) => category.label),
          datasets: [
            {
              label: "Legacy",
              data: opsCategories.map((category) => category.legacy),
              backgroundColor: "rgba(95, 111, 100, 0.85)",
              borderRadius: 0,
              borderSkipped: false,
              barThickness: 16,
              maxBarThickness: 34,
              categoryPercentage: 0.9,
              barPercentage: 0.8
            },
            {
              label: "Retrofitted",
              data: opsCategories.map((category) => category.retro),
              backgroundColor: "#FAB018",
              borderRadius: 0,
              borderSkipped: false,
              barThickness: 16,
              maxBarThickness: 34,
              categoryPercentage: 0.9,
              barPercentage: 0.8
            }
          ]
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            datalabels: {
              color: "#132218",
              anchor: "end",
              align: "end",
              clamp: true,
              font: { weight: "700", size: 11 },
              formatter: (value) => this.fmtMoney(value)
            },
            legend: {
              display: false,
              position: "top",
              align: "start",
              labels: {
                boxWidth: 18,
                boxHeight: 3,
                padding: 14
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.dataset.label}: ${this.fmtMoney(context.raw)}`
              }
            }
          },
          scales: {
            x: {
              beginAtZero: true,
              ticks: {
                callback: (value) => this.fmtMoney(value)
              },
              grid: {
                color: "rgba(89, 116, 95, 0.14)"
              }
            },
            y: {
              grid: {
                display: false
              }
            }
          }
        }
      });
    }

    const labelsBreakdown = ["Charging", "Labor", "Towing", "Battery", "Grounds"];
    const legacyBreakdown = [
      metrics.legacyCharging,
      metrics.legacyLabor,
      metrics.legacyTowDamage,
      metrics.legacyBattery,
      metrics.legacyGroundsDamage
    ];
    const retroBreakdown = [
      metrics.retroCharging,
      metrics.retroLabor,
      metrics.retroTowDamage,
      metrics.retroBattery,
      metrics.retroGroundsDamage
    ];

    const makeBreakdownChart = (targetId, existingChart, values) => {
      const target = this.ref(targetId);
      if (existingChart) {
        existingChart.destroy();
      }
      if (!target) {
        return undefined;
      }

      return new window.Chart(target, {
        type: "doughnut",
        data: {
          labels: labelsBreakdown,
          datasets: [
            {
              data: values,
              backgroundColor: COLOR_SET,
              borderWidth: 0
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          aspectRatio: 1,
          cutout: "55%",
          plugins: {
            legend: {
              position: "bottom",
              align: "start",
              labels: {
                boxWidth: 24,
                boxHeight: 10,
                padding: 14,
                font: {
                  size: 12,
                  weight: "400"
                },
                filter: (legendItem) => values[legendItem.index] > 0
              }
            },
            tooltip: {
              callbacks: {
                label: (context) => `${context.label}: ${this.fmtMoney(context.raw)}`
              }
            },
            datalabels: {
              color: "#ffffff",
              font: { weight: "700", size: 11 },
              formatter(value, context) {
                const dataset = context.chart.data.datasets[0].data;
                const total = dataset.reduce((acc, cur) => acc + cur, 0);
                if (!total || value <= 0) {
                  return "";
                }
                const pct = (value / total) * 100;
                return pct >= 6 ? `${pct.toFixed(0)}%` : "";
              }
            }
          }
        }
      });
    };

    this.#legacyBreakdownChart = makeBreakdownChart("chartLegacyBreakdown", this.#legacyBreakdownChart, legacyBreakdown);
    this.#retroBreakdownChart = makeBreakdownChart("chartRetroBreakdown", this.#retroBreakdownChart, retroBreakdown);
  }

  calculate() {
    const fleetSize = this.getNumber("fleetSize");
    const hourlyLaborCost = this.getNumber("hourlyLaborCost");
    const dailyChargingCost = this.getNumber("dailyChargingCost");
    const towCarts = this.ref("towCarts")?.value === "yes";
    const legacyLaborPerCart = this.getNumber("legacyLaborPerCart");
    const legacyBatteryInterval = this.getNumber("legacyBatteryInterval");
    const batteryReplacementCost = this.getNumber("batteryReplacementCost");
    const retrofitKitCost = this.getNumber("retrofitKitCost");
    const postRetroDailyCharging = this.getNumber("postRetroDailyCharging");
    const postRetroAnnualMaintenance = this.getNumber("postRetroAnnualMaintenance");
    const retrofitLifespan = this.getNumber("retrofitLifespan");

    const legacyCharging = fleetSize * dailyChargingCost * 365;
    const retroCharging = fleetSize * postRetroDailyCharging * 365;

    const legacyLabor = fleetSize * legacyLaborPerCart;
    const retroLaborPerCart = postRetroAnnualMaintenance + (hourlyLaborCost * ASSUMED_RETRO_LABOR_HOURS_PER_CART);
    const retroLabor = fleetSize * retroLaborPerCart;

    const legacyTowDamage = towCarts ? fleetSize * TOW_DAMAGE_COST_PER_CART_PER_YEAR : 0;
    const retroTowDamage = 0;

    const legacyGroundsDamage = towCarts ? fleetSize * GROUNDS_DAMAGE_COST_PER_CART_PER_YEAR : 0;
    const retroGroundsDamage = 0;

    const legacyBattery = legacyBatteryInterval > 0
      ? (fleetSize * batteryReplacementCost) / legacyBatteryInterval
      : 0;
    const retroBattery = 0;

    const legacyTotal =
      legacyCharging +
      legacyLabor +
      legacyTowDamage +
      legacyGroundsDamage +
      legacyBattery;

    const retroTotal =
      retroCharging +
      retroLabor +
      retroTowDamage +
      retroGroundsDamage +
      retroBattery;

    const initialInvestment = fleetSize * retrofitKitCost;
    const netInitial = initialInvestment;
    const annualSavings = legacyTotal - retroTotal;
    const lifetimeGross = annualSavings * retrofitLifespan;
    const lifetimeNet = lifetimeGross - netInitial;
    const costReduction = legacyTotal > 0 ? (annualSavings / legacyTotal) * 100 : 0;
    const roiMultiple = netInitial > 0 ? (lifetimeNet / netInitial) : 0;
    const roiPercent = roiMultiple * 100;

    const paybackRaw = annualSavings > 0 ? netInitial / annualSavings : Number.POSITIVE_INFINITY;
    const paybackYears = Number.isFinite(paybackRaw) ? Math.ceil(paybackRaw) : NaN;

    this.updateCell("outInitialInvestment", initialInvestment);
    this.updateCell("outNetInitial", netInitial);
    this.updateCell("outAnnualSavings", annualSavings);
    this.updateCell("outAnnualSavingsOps", annualSavings);
    this.updateCell("outLifetimeGross", lifetimeGross);
    this.updateCell("outLifetimeNet", lifetimeNet);
    this.updateCell("outPayback", paybackYears, this.fmtYears.bind(this));

    this.updateCell("outAnnualSavingsTop", annualSavings);
    this.updateCell("outLifetimeNetTop", lifetimeNet);
    this.updateCell("outPaybackTop", paybackYears, this.fmtYears.bind(this));

    const years = Math.max(1, Math.round(retrofitLifespan));
    this.updateCell("outLegacyTenYear", legacyTotal * years);
    this.updateCell("outRetroTenYear", retroTotal * years);
    this.updateCell("outTenYearSavings", annualSavings * years);
    this.updateCell("outTenYearSavingsBadge", annualSavings * years, this.fmtMoneyCompact.bind(this));

    const yearsText = String(years);
    ["outLifetimeYears", "outLifetimeYearsSummary", "outLifetimeNetYears", "outRoiYears"].forEach((id) => {
      const node = this.ref(id);
      if (node) {
        node.textContent = yearsText;
      }
    });

    const paybackMonthsText = Number.isFinite(paybackYears) ? String(Math.max(0, paybackYears * 12)) : "N/A";
    ["outPaybackMonths", "outPaybackMonthsSummary"].forEach((id) => {
      const node = this.ref(id);
      if (node) {
        node.textContent = paybackMonthsText;
      }
    });

    this.updateCellText("outRoiMultipleTop", Number.isFinite(roiMultiple) ? `${roiMultiple.toFixed(2)}x ROI` : "N/A");
    this.updateCellText("outRoiMultipleSummary", Number.isFinite(roiMultiple) ? `${roiMultiple.toFixed(2)}x` : "N/A");
    this.updateCellText("outRoiPct", Number.isFinite(roiPercent)
      ? `${this.#percentFormatter.format(Math.max(0, roiPercent))}%`
      : "N/A");

    this.updateCellText("outCostReductionPct", `${Math.max(0, costReduction).toFixed(1)}%`);

    const costReductionBar = this.ref("outCostReductionBar");
    if (costReductionBar) {
      costReductionBar.style.width = `${Math.min(100, Math.max(0, costReduction))}%`;
    }

    this.renderCharts({
      retrofitLifespan,
      legacyTotal,
      retroTotal,
      legacyCharging,
      legacyLabor,
      legacyTowDamage,
      legacyBattery,
      legacyGroundsDamage,
      retroCharging,
      retroLabor,
      retroTowDamage,
      retroBattery,
      retroGroundsDamage
    });
  }

  updateCellText(id, text) {
    const node = this.ref(id);
    if (node) {
      node.textContent = text;
    }
  }
}

if (!customElements.get("fleet-roi-calculator")) {
  customElements.define("fleet-roi-calculator", FleetRoiCalculator);
}
